import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../constants/environment';
import { useContext, useState } from 'react';
import SessionContext from '../contexts/SessionContext';

export interface PullCccLocationsInput {
	organizationId: string;
	username: string;
	password: string;
}

export interface CccLocation {
	id: string;
	name: string;
	address?: string;
	city?: string;
	state?: string;
	zip?: string;
}

interface SubmitResponse {
	organizationId: string;
}

interface PullStatusResponse {
	organizationId: string;
	target: string;
	status: 'pending' | 'in-progress' | 'completed' | 'failed' | null;
	locations: CccLocation[] | null;
	error: string | null;
}

export const usePullCccLocations = () => {
	const sessionContext = useContext(SessionContext);
	const [organizationId, setOrganizationId] = useState<string | null>(null);

	const submit = useMutation({
		mutationFn: async (input: PullCccLocationsInput) => {
			const { data } = await axios.post<SubmitResponse>(
				`${API_URL}/onboarding/pull-ccc-locations`,
				input,
				{ headers: { Authorization: sessionContext?.sessionId ?? '' } }
			);
			setOrganizationId(data.organizationId);
			return data;
		},
	});

	const status = useQuery({
		queryKey: ['pull-ccc-status', organizationId],
		queryFn: async () => {
			const { data } = await axios.get<PullStatusResponse>(
				`${API_URL}/onboarding/pull-status`,
				{ params: { organizationId, target: 'ccc' }, headers: { Authorization: sessionContext?.sessionId ?? '' } }
			);
			return data;
		},
		enabled: !!organizationId,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (data?.status === 'completed' || data?.status === 'failed') {
				return false;
			}
			return 3000;
		},
	});

	const reset = () => {
		setOrganizationId(null);
	};

	return {
		submit,
		status,
		organizationId,
		reset,
		isPolling: !!organizationId && status.data?.status !== 'completed' && status.data?.status !== 'failed',
		locations: status.data?.locations ?? null,
		error: status.data?.error ?? null,
		isComplete: status.data?.status === 'completed',
		isFailed: status.data?.status === 'failed',
	};
};
